// bruteForceGuard — client-side brute-force throttle for sign-in.
//
// Supabase Auth has server-side rate-limits, but a fast local check
// stops the attacker from flooding the network call AND gives the
// honest user a clear "wait X seconds" message instead of a generic
// 429.
//
// Model: per-email sliding window. 5 failures within 5 minutes locks
// that email for 15 minutes. Successful sign-in clears the counter.

const FAIL_KEY_PREFIX = 'fw.auth.fails.';
const LOCK_KEY_PREFIX = 'fw.auth.lock.';
const MAX_FAILS = 5;
const WINDOW_MS = 5 * 60 * 1000;       // 5 min
const LOCKOUT_MS = 15 * 60 * 1000;     // 15 min

function emailKey(email: string): string {
  return email.trim().toLowerCase();
}

interface FailRecord {
  /** Timestamps of recent failures within WINDOW_MS. */
  timestamps: number[];
}

function readFails(email: string): FailRecord {
  try {
    const raw = localStorage.getItem(FAIL_KEY_PREFIX + emailKey(email));
    if (!raw) return { timestamps: [] };
    const parsed = JSON.parse(raw) as FailRecord;
    if (!Array.isArray(parsed.timestamps)) return { timestamps: [] };
    const cutoff = Date.now() - WINDOW_MS;
    return { timestamps: parsed.timestamps.filter((t) => t > cutoff) };
  } catch {
    return { timestamps: [] };
  }
}

function writeFails(email: string, rec: FailRecord): void {
  try {
    localStorage.setItem(FAIL_KEY_PREFIX + emailKey(email), JSON.stringify(rec));
  } catch {
    /* ignore */
  }
}

function setLock(email: string): void {
  try {
    localStorage.setItem(LOCK_KEY_PREFIX + emailKey(email), String(Date.now() + LOCKOUT_MS));
  } catch {
    /* ignore */
  }
}

function readLock(email: string): number {
  try {
    const raw = localStorage.getItem(LOCK_KEY_PREFIX + emailKey(email));
    if (!raw) return 0;
    const until = Number(raw);
    if (Number.isNaN(until) || until <= Date.now()) {
      localStorage.removeItem(LOCK_KEY_PREFIX + emailKey(email));
      return 0;
    }
    return until;
  } catch {
    return 0;
  }
}

/**
 * Returns 0 when sign-in is permitted, otherwise the number of seconds
 * left until the lockout expires.
 */
export function getLockoutSecondsLeft(email: string): number {
  const until = readLock(email);
  if (until === 0) return 0;
  return Math.max(0, Math.ceil((until - Date.now()) / 1000));
}

/** Record a failed attempt; returns the resulting lockout-seconds (0 = no lockout). */
export function recordSignInFailure(email: string): number {
  if (!email) return 0;
  const rec = readFails(email);
  rec.timestamps.push(Date.now());
  writeFails(email, rec);
  if (rec.timestamps.length >= MAX_FAILS) {
    setLock(email);
    return Math.ceil(LOCKOUT_MS / 1000);
  }
  return 0;
}

/** Wipe the per-email counters on successful sign-in. */
export function clearSignInThrottle(email: string): void {
  try {
    localStorage.removeItem(FAIL_KEY_PREFIX + emailKey(email));
    localStorage.removeItem(LOCK_KEY_PREFIX + emailKey(email));
  } catch {
    /* ignore */
  }
}

export const BRUTE_FORCE_CONFIG = {
  MAX_FAILS,
  WINDOW_MS,
  LOCKOUT_MS,
} as const;
